import React, { useCallback, useMemo, useState } from 'react';
import { Trans } from '@lingui/macro';
import { AlertWarning } from 'components/base/alert/alert';
import { BackButton } from 'components/base/back-button/BackButton';
import { DatetimeField } from 'components/base/form/datefield/datefield';
import EditableCard from 'components/base/form/editablecard/editablecard';
import { FieldError } from 'components/base/form/errornotice/errornotice';
import FormChangeEffect from 'components/base/form/formchangeeffect/formchangeeffect';
import { IntervalField } from 'components/base/form/interval/intervalfield';
import { SimpleSelectField } from 'components/base/form/simpleselect/simpleselectfield';
import { menuItemsFromEnum } from 'components/base/i18n/menuItemsFromEnum';
import Loading from 'components/base/loading/loading';
import { AreaSingleMenu } from 'components/modules/async-menu/AreaMenu';
import { ObsPointMenu } from 'components/modules/async-menu/ObsPointMenu';
import PageStandard from 'components/modules/pagestandard/pagestandard';
import { FormikErrors, FormikHelpers, FormikProps } from 'formik';
import { getExpectedFields } from 'util/backendapi/error';
import { showErrorsInFormik } from 'util/backendapi/error-formik';
import { TimeLagMonitor_MODE } from 'util/backendapi/types/Enum';
import {
  TimeLagMonitor_POST,
  TimeLagMonitorDecorated,
} from 'util/backendapi/types/Model';
import { formatDatetimeForDisplay } from 'util/dates';
import { useIsMounted } from 'util/hooks';
import { IntervalDisplay } from 'components/base/i18n/IntervalDisplay';
import { TransEnum } from 'components/base/i18n/TransEnum';
import { ButtonShowConfirmation } from 'components/base/confirmation/ButtonShowConfirmation';
import { SimpleSelectOption } from 'components/base/form/simpleselect/simpleselect';

export type TimeLagMonitorEditFormValues = Omit<TimeLagMonitor_POST, 'mode'> & {
  id: number | null;
  mode: TimeLagMonitor_MODE | null;
  output_observation_point_detail: SimpleSelectOption<number>[];
};

export interface EditTimeLagMonitorViewProps {
  timeLagMonitor: TimeLagMonitorDecorated | null;
  isNewTimeLagMonitor: boolean;
  isLoading: boolean;
  errorMessage: string | null;
  initialEditingState: boolean;
  onSubmit: (values: TimeLagMonitorEditFormValues) => Promise<void>;
  onCancel: () => void;
}

const makeInitialValues = (
  timeLagMonitor: TimeLagMonitorDecorated | null
): TimeLagMonitorEditFormValues => {
  if (timeLagMonitor) {
    return {
      ...timeLagMonitor,
      area: timeLagMonitor.area.id,
      observation_point_a: timeLagMonitor.observation_point_a.id,
      observation_point_b: timeLagMonitor.observation_point_b.id,
      output_observation_point: timeLagMonitor.output_observation_point.id,
      output_observation_point_detail: [],
    };
  }

  return {
    id: null,
    area: 0,
    observation_point_a: 0,
    observation_point_b: 0,
    output_observation_point: 0,
    mode: null,
    start_datetime: '',
    cycle_length: '',
    resample_frequency: '',
    window_length: '',
    output_observation_point_detail: [],
  };
};

const requiredFields: Array<keyof TimeLagMonitorEditFormValues> = [
  'area',
  'observation_point_a',
  'observation_point_b',
  'output_observation_point',
  'mode',
  'start_datetime',
  'cycle_length',
  'resample_frequency',
  'window_length',
];

const validateForm = (
  values: TimeLagMonitorEditFormValues
): FormikErrors<TimeLagMonitorEditFormValues> => {
  let errors: FormikErrors<TimeLagMonitorEditFormValues> = {};

  requiredFields.forEach(function (fieldName) {
    if (!values[fieldName]) {
      errors[fieldName] = (<Trans>This field is required</Trans>) as any;
    }
  });

  return errors;
};

const UpdateConfirmationButton = (
  formik: FormikProps<TimeLagMonitorEditFormValues>
) => {
  return (
    <ButtonShowConfirmation
      name={`time-lag-edit-submit-confirm-button`}
      data-testid={`time-lag-edit-submit-confirm-button`}
      iconType="icon-save"
      disabled={formik.isSubmitting}
      destructive={true}
      onClick={async () => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
          // Cause the errors to be displayed (validateForm doesn't do this itself)
          // the errors will prevent the form actually submitting
          formik.submitForm();
          return false;
        }
        return true;
      }}
      onConfirm={async () => {
        await formik.submitForm();
      }}
      content={
        <Trans>
          Changing these settings will retire the current effective readings of{' '}
          <strong>
            {formik.values.output_observation_point_detail[0]?.label}
          </strong>
          . This action is not reversible.
        </Trans>
      }
      okBtnText={<Trans>Yes, confirm</Trans>}
    >
      <Trans>Save</Trans>
    </ButtonShowConfirmation>
  );
};

export function EditTimeLagMonitorView(props: EditTimeLagMonitorViewProps) {
  const {
    timeLagMonitor,
    isNewTimeLagMonitor,
    isLoading,
    initialEditingState,
    errorMessage,
    onSubmit,
    onCancel,
  } = props;

  const [isEditing, setIsEditing] = useState(initialEditingState);

  const isMounted = useIsMounted();

  const handleSubmit = useCallback(
    async (
      values: TimeLagMonitorEditFormValues,
      formik: FormikHelpers<TimeLagMonitorEditFormValues>
    ) => {
      try {
        await onSubmit(values);
        if (isMounted()) {
          setIsEditing(false);
        }
      } catch (e) {
        if (isMounted()) {
          showErrorsInFormik(formik, e, getExpectedFields(values));
          formik.setSubmitting(false);
        }
      }
    },
    [onSubmit, isMounted, setIsEditing]
  );

  const modeOptions = useMemo(
    () => menuItemsFromEnum('TimeLagMonitor_MODE'),
    []
  );

  return (
    <PageStandard
      name="edit-time-lag-monitor"
      header={
        isNewTimeLagMonitor ? (
          <Trans>Create time lag monitor</Trans>
        ) : (
          <Trans>Edit time lag monitor</Trans>
        )
      }
      subHeader={
        timeLagMonitor
          ? `${timeLagMonitor.observation_point_a.code}, ${timeLagMonitor.observation_point_b.code}`
          : null
      }
    >
      <div className="page-content-header-with-back-button-wrapper">
        <BackButton defaultBackUrl="/time-lag-monitors" />
        <div className="page-content-header">
          {isLoading ? (
            <Loading />
          ) : errorMessage ? (
            <AlertWarning>{errorMessage}</AlertWarning>
          ) : !timeLagMonitor && !isNewTimeLagMonitor ? (
            <AlertWarning>
              <Trans>Time lag monitor not found</Trans>
            </AlertWarning>
          ) : (
            <EditableCard<TimeLagMonitorEditFormValues>
              name="general"
              header={<Trans>General</Trans>}
              isEditMode={isEditing}
              startEditing={() => setIsEditing(true)}
              stopEditing={
                isNewTimeLagMonitor ? onCancel : () => setIsEditing(false)
              }
              saveButton={
                !isNewTimeLagMonitor ? UpdateConfirmationButton : undefined
              }
              onSubmit={handleSubmit}
              initialValues={makeInitialValues(timeLagMonitor)}
              validate={validateForm}
              render={({ CardSectionComponent, formik }) => {
                return (
                  <>
                    {formik.status}
                    <CardSectionComponent
                      name="input-observation-points"
                      header={<Trans>Input observation points</Trans>}
                      fields={[
                        {
                          name: 'area',
                          label: <Trans>Area</Trans>,
                          content: isEditing ? (
                            <>
                              <AreaSingleMenu
                                name="area"
                                isDisabled={formik.isSubmitting}
                                autoFocus={true}
                              />
                              <FieldError name="area" />
                              <FormChangeEffect
                                onChange={(previous) => {
                                  if (
                                    formik.values.area !== previous.values.area
                                  ) {
                                    formik.setFieldValue(
                                      'observation_point_a',
                                      0
                                    );
                                    formik.setFieldValue(
                                      'observation_point_b',
                                      0
                                    );
                                    formik.setFieldValue(
                                      'output_observation_point',
                                      0
                                    );
                                  }
                                }}
                              />
                            </>
                          ) : (
                            timeLagMonitor?.area.code
                          ),
                        },
                        {
                          name: 'observation_point_a',
                          label: <Trans>Observation point A</Trans>,
                          content: isEditing ? (
                            <>
                              <ObsPointMenu
                                name="observation_point_a"
                                isDisabled={
                                  formik.isSubmitting || !formik.values.area
                                }
                                area={formik.values.area}
                              />
                              <FieldError name="observation_point_a" />
                            </>
                          ) : (
                            timeLagMonitor?.observation_point_a.code
                          ),
                        },
                        {
                          name: 'observation_point_b',
                          label: <Trans>Observation point B</Trans>,
                          content: isEditing ? (
                            <>
                              <ObsPointMenu
                                name="observation_point_b"
                                isDisabled={
                                  formik.isSubmitting || !formik.values.area
                                }
                                area={formik.values.area}
                              />
                              <FieldError name="observation_point_b" />
                            </>
                          ) : (
                            timeLagMonitor?.observation_point_b.code
                          ),
                        },
                      ]}
                    />
                    <CardSectionComponent
                      name="data-window"
                      header={<Trans>Data window</Trans>}
                      fields={[
                        {
                          name: 'start_datetime',
                          label: <Trans>Start date</Trans>,
                          content: isEditing ? (
                            <>
                              <DatetimeField name="start_datetime" />
                              <FieldError name="start_datetime" />
                            </>
                          ) : (
                            formatDatetimeForDisplay(
                              timeLagMonitor?.start_datetime,
                              timeLagMonitor?.area.time_zone.name
                            )
                          ),
                        },
                        {
                          name: 'cycle_length',
                          label: <Trans>Cycle length</Trans>,
                          formGroupClassName: 'form-group-interval-select',
                          content: isEditing ? (
                            <>
                              <IntervalField name="cycle_length" />
                              <FieldError name="cycle_length" />
                            </>
                          ) : (
                            <IntervalDisplay
                              value={timeLagMonitor?.cycle_length}
                            />
                          ),
                        },
                        {
                          name: 'resample_frequency',
                          label: <Trans>Resample frequency</Trans>,
                          formGroupClassName: 'form-group-interval-select',
                          content: isEditing ? (
                            <>
                              <IntervalField name="resample_frequency" />
                              <FieldError name="resample_frequency" />
                            </>
                          ) : (
                            <IntervalDisplay
                              value={timeLagMonitor?.resample_frequency}
                            />
                          ),
                        },
                        {
                          name: 'window_length',
                          label: <Trans>Window length</Trans>,
                          formGroupClassName: 'form-group-interval-select',
                          content: isEditing ? (
                            <>
                              <IntervalField name="window_length" />
                              <FieldError name="window_length" />
                            </>
                          ) : (
                            <IntervalDisplay
                              value={timeLagMonitor?.window_length}
                            />
                          ),
                        },
                      ]}
                    />
                    <CardSectionComponent
                      name="output"
                      header={<Trans>Output</Trans>}
                      fields={[
                        {
                          name: 'output_observation_point',
                          label: <Trans>Output observation point</Trans>,
                          content: isEditing ? (
                            <>
                              <ObsPointMenu
                                name="output_observation_point"
                                isDisabled={
                                  formik.isSubmitting || !formik.values.area
                                }
                                area={formik.values.area}
                                detailsName="output_observation_point_detail"
                              />
                              <FieldError name="output_observation_point" />
                            </>
                          ) : (
                            timeLagMonitor?.output_observation_point.code
                          ),
                        },
                        {
                          name: 'mode',
                          label: <Trans>Mode</Trans>,
                          content: isEditing ? (
                            <>
                              <SimpleSelectField
                                name="mode"
                                options={modeOptions}
                              />
                              <FieldError name="mode" />
                            </>
                          ) : (
                            <TransEnum
                              enum="TimeLagMonitor_MODE"
                              value={timeLagMonitor?.mode}
                            />
                          ),
                        },
                      ]}
                    />
                  </>
                );
              }}
            />
          )}
        </div>
      </div>
    </PageStandard>
  );
}
